import React, { useState } from "react";
import { Mail, Check } from "lucide-react";
import EmailService from "../services/EmailService";
import ProgressIndicator from "../common/ProgressIndicator";

const EmailOutreach = ({
  earlyAccessData,
  contactFormData,
  updatesSignupData,
  onSendEmail,
}) => {
  const [emailData, setEmailData] = useState({
    title: "",
    message: "",
    recipientGroup: "early-access",
    testEmail: "",
  });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showTestEmailAlert, setShowTestEmailAlert] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progressText, setProgressText] = useState("Initializing...");

  const getSelectedEmails = () => {
    let emails = [];
    switch (emailData.recipientGroup) {
      case "early-access":
        emails = earlyAccessData.map((row) => row[1]);
        break;
      case "contact-form":
        emails = contactFormData.map((row) => row[2]);
        break;
      case "updates-signup":
        emails = updatesSignupData.map((row) => row[2]);
        break;
      default:
        emails = [];
    }
    return emails;
  };

 const sendOutEmails = async (emails, companyName = "ZetaFlow Team", emailData) => {
  setLoading(true);
  setProgressText("Started sending out email messages...");

  try {
    await Promise.all(
      emails.map(async (email) => {
        try {
          const emailRequest = {
            ToEmail: email,
            Subject: emailData.title || "Invitation to ZetaFlow Productivity App",
            Content: `
              <html>
                <body>
                  <p>Dear ${email},</p>
                  <p>You have been invited to the <strong>ZetaFlow Productivity App</strong></p>
                  <p>by the ${companyName}.</strong></p>
                  <p>${emailData.message}</p>
                </body>
              </html>
            `,
          };

          await EmailService.sendEmail(emailRequest);
        } catch (error) {
          console.error(`Error sending invitation to ${email}:`, error);
          setLoading(false);
          throw error;
        }
      })
    );

    setProgressText("Emails sent successfully");
    setLoading(false);

    // Clear the emailData after sending
    setEmailData({
      title: "",
      message: "",
      recipientGroup: "early-access",
      testEmail: "",
    });

    return true;
  } catch (error) {
    setProgressText("Error sending Emails");
    setLoading(false);
    console.error("Error sending Emails:", error);
    throw error;
  }
};

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    const recipientEmails = getSelectedEmails();
    if (recipientEmails.length > 0) {
      setShowConfirmDialog(true);
    }
  };

  // const handleEmailSubmit = async (e) => {
  //   e.preventDefault();
  //   const recipientEmails = getSelectedEmails();
  //   if (recipientEmails.length > 0) {
  //     setShowConfirmDialog(true);

  //     // Wait for confirmation dialog to be accepted
  //     const confirmed = await showConfirmDialog();
  //     if (confirmed) {
  //       try {
  //         await sendOutEmails(recipientEmails, "ZetaFlow Team", emailData);
  //         console.log("Emails sent successfully");
  //       } catch (error) {
  //         console.error("Error sending emails:", error);
  //       }
  //     }
  //   }
  // };

  const handleConfirmedSubmit = () => {
    // setLoading(true);
    // setProgressText("Started sending out email messages...");
    const recipientEmails = getSelectedEmails();
   const result =  onSendEmail({
      ...emailData,
      recipientEmails,
    });
    setShowConfirmDialog(false);
    if(result){
      //clear the emailData after sending
      setEmailData({
        title: "",
        message: "",
        recipientGroup: "early-access",
        testEmail: "",
      });
      setShowConfirmDialog(false);
      setLoading(false);
      setProgressText("Emails sent successfully");
    }
    //setLoading(false);
    //setProgressText("Emails sent successfully");
  };

  const handleTestEmail = async () => {
    if (emailData.testEmail) {
      setLoading(true);
      setProgressText("Sending test email...");
      const emailRequest = {
        ToEmail: emailData.testEmail,
        Subject: emailData.title || "Test Email from ZetaFlow",
        Content: `
          <html>
            <body>
              <p>Dear ${emailData.testEmail},</p>
              <p>You have been invited to the <strong>ZetaFlow Productivity App</strong></p>
              <p>${emailData.message}</p>
            </body>
          </html>
        `,
      };
  
      try {
        await EmailService.sendEmail(emailRequest);
        setShowTestEmailAlert(true);
        setTimeout(() => setShowTestEmailAlert(false), 3000);
        console.log("Test email sent successfully");
  
        // Clear the emailData after sending
        setEmailData({
          title: "",
          message: "",
          recipientGroup: "early-access",
          testEmail: "",
        });
        setProgressText("Test email sent successfully");
        setLoading(false);
      } catch (error) {
        console.error("Error sending test email:", error);
        setProgressText("Error sending test email:", error);
        setLoading(false);
      }
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-[#4C4D5E] mb-6 text-xl font-semibold">
        Email Outreach
      </h2>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-[#4C4D5E] text-lg font-semibold">
            Compose Email
          </h3>
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className="text-[#0C60F2] text-sm font-medium hover:text-[#0A4ED1]"
          >
            {previewMode ? "Edit Message" : "Preview Recipients"}
          </button>
        </div>

        {previewMode ? (
          <PreviewSection getSelectedEmails={getSelectedEmails} />
        ) : (
          <EmailForm
            emailData={emailData}
            setEmailData={setEmailData}
            onSubmit={handleEmailSubmit}
            onTestEmail={handleTestEmail}
            earlyAccessData={earlyAccessData}
            contactFormData={contactFormData}
            updatesSignupData={updatesSignupData}
          />
        )}
      </div>

      {showConfirmDialog && (
        <ConfirmDialog
          recipientCount={getSelectedEmails().length}
          onCancel={() => setShowConfirmDialog(false)}
          onConfirm={handleConfirmedSubmit}
        />
      )}

      {showTestEmailAlert && <TestEmailAlert />}
      <ProgressIndicator text={progressText} isLoading={loading} />
    </div>
  );
};

const PreviewSection = ({ getSelectedEmails }) => (
  <div className="space-y-4">
    <h4 className="font-medium text-[#4C4D5E]">Selected Recipients Preview</h4>
    <div className="bg-gray-50 p-4 rounded-md">
      <p className="text-sm text-[#4C4D5E] mb-2">
        Total Recipients: {getSelectedEmails().length}
      </p>
      <div className="space-y-2">
        {getSelectedEmails()
          .slice(0, 5)
          .map((email, idx) => (
            <div key={idx} className="flex items-center space-x-2">
              <Mail className="h-4 w-4 text-[#0C60F2]" />
              <span className="text-sm">{email}</span>
            </div>
          ))}
        {getSelectedEmails().length > 5 && (
          <p className="text-sm text-gray-500 italic">
            And {getSelectedEmails().length - 5} more recipients...
          </p>
        )}
      </div>
    </div>
  </div>
);

const EmailForm = ({
  emailData,
  setEmailData,
  onSubmit,
  onTestEmail,
  earlyAccessData,
  contactFormData,
  updatesSignupData,
}) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <div>
      <label className="block mb-2 text-sm font-medium text-[#4C4D5E]">
        Select Recipients
      </label>
      <select
        value={emailData.recipientGroup}
        onChange={(e) =>
          setEmailData({ ...emailData, recipientGroup: e.target.value })
        }
        className="w-full px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0C60F2] focus:border-transparent bg-white text-sm"
      >
        <option value="early-access">
          Early Access Signups ({earlyAccessData.length} recipients)
        </option>
        <option value="contact-form">
          Contact Form Submissions ({contactFormData.length} recipients)
        </option>
        <option value="updates-signup">
          Updates Signups ({updatesSignupData.length} recipients)
        </option>
      </select>
    </div>

    <div className="space-y-4 border-t border-gray-200 pt-4">
      <h4 className="font-medium text-[#4C4D5E]">Test Email (Optional)</h4>
      <div className="flex space-x-2">
        <input
          type="email"
          value={emailData.testEmail}
          onChange={(e) =>
            setEmailData({ ...emailData, testEmail: e.target.value })
          }
          placeholder="Enter your email for testing"
          className="flex-1 px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0C60F2] focus:border-transparent text-sm"
        />
        <button
          type="button"
          onClick={onTestEmail}
          className="px-4 py-2 bg-gray-100 text-[#4C4D5E] rounded-md hover:bg-gray-200 transition-colors text-sm"
        >
          Send Test
        </button>
      </div>
    </div>

    <div className="space-y-4 border-t border-gray-200 pt-4">
      <InputField
        label="Email Title"
        value={emailData.title}
        onChange={(e) => setEmailData({ ...emailData, title: e.target.value })}
      />
      <TextareaField
        label="Email Message"
        value={emailData.message}
        onChange={(e) =>
          setEmailData({ ...emailData, message: e.target.value })
        }
      />
      <button
        type="submit"
        className="w-full bg-[#0C60F2] text-white px-4 py-2 rounded-md hover:bg-[#0A4ED1] transition-colors text-sm font-semibold"
      >
        Send Email
      </button>
    </div>
  </form>
);

const InputField = ({ label, value, onChange }) => (
  <div>
    <label className="block mb-2 text-sm font-medium text-[#4C4D5E]">
      {label}
    </label>
    <input
      type="text"
      value={value}
      onChange={onChange}
      className="w-full px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0C60F2] focus:border-transparent text-sm"
    />
  </div>
);

const TextareaField = ({ label, value, onChange }) => (
  <div>
    <label className="block mb-2 text-sm font-medium text-[#4C4D5E]">
      {label}
    </label>
    <textarea
      value={value}
      onChange={onChange}
      className="w-full px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0C60F2] focus:border-transparent text-sm"
      rows="4"
    />
  </div>
);

const ConfirmDialog = ({ recipientCount, onCancel, onConfirm }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6">
      <h3 className="text-lg font-semibold mb-2">Confirm Email Send</h3>
      <p className="text-[#4C4D5E] mb-6">
        You are about to send an email to {recipientCount} recipients. This
        action cannot be undone. Are you sure you want to proceed?
      </p>
      <div className="flex justify-end space-x-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-[#4C4D5E] hover:bg-gray-100 rounded-md"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 bg-[#0C60F2] text-white rounded-md hover:bg-[#0A4ED1]"
        >
          Yes, Send Email
        </button>
      </div>
    </div>
  </div>
);

const TestEmailAlert = () => (
  <div className="fixed bottom-4 right-4 w-96 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2">
    <Check className="h-4 w-4 text-green-600" />
    <p className="text-green-800">Test email sent successfully!</p>
  </div>
);

export default EmailOutreach;
